import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Plus, LayoutDashboard, Calendar as CalendarIcon, Table2,
  Search, X, Filter
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { pipelineService } from '@/lib/pipelineService';
import { usePipelineState } from '@/hooks/usePipelineState';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { PipelineBoard } from '@/components/pipeline/PipelineBoard';
import { NewDealDialog } from '@/components/pipeline/NewDealDialog';
import { DealCalendarView } from '@/components/pipeline/DealCalendarView';
import { DealTableView } from '@/components/pipeline/DealTableView';
import { DealDetailDialog } from '@/components/pipeline/DealDetailDialog';
import type { Deal, Pipeline } from '@/components/pipeline/types';
import { HeaderSettingsDialog, HeaderConfig } from '@/components/kanban/HeaderSettingsDialog';

interface PipelinesProps {
  isGlobalDarkMode?: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

export default function Pipelines({
  isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false
}: PipelinesProps) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'kanban' | 'calendar' | 'table'>('kanban');
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [addDealOpen, setAddDealOpen] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Load pipelines and auto-create default if none exist
  const loadPipelines = async () => {
    if (!currentWorkspace || !user) return;
    try {
      setLoading(true);
      let data = await pipelineService.getPipelinesByWorkspace(currentWorkspace.id);

      // Auto-create default pipeline if none exist
      if (data.length === 0) {
        await pipelineService.createPipeline(
          currentWorkspace.id,
          user.id,
          'Sales Pipeline',
          'Default sales pipeline',
          { type: 'gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }
        );
        pipelineService.clearCache();
        data = await pipelineService.getPipelinesByWorkspace(currentWorkspace.id);
      }

      setPipelines(data);

      // Select first pipeline if none selected or selected no longer exists
      if (!selectedPipelineId || !data.find(p => p.id === selectedPipelineId)) {
        setSelectedPipelineId(data[0]?.id || null);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load pipelines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPipelines(); }, [currentWorkspace?.id, user?.id]); // eslint-disable-line

  const currentPipeline = pipelines.find(p => p.id === selectedPipelineId) || pipelines[0];

  const pipelineConfig: HeaderConfig = {
    title: currentPipeline?.title || 'Sales Pipeline',
    description: currentPipeline?.description || 'Default sales pipeline',
    background: currentPipeline?.background || {
      type: 'gradient',
      value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    hideBanner: currentPipeline?.hideBanner ?? true,
  };

  const handleUpdatePipelineConfig = async (next: HeaderConfig) => {
    if (!selectedPipelineId) return;
    try {
      await pipelineService.updatePipeline(selectedPipelineId, {
        title: next.title,
        description: next.description,
        background: next.background,
        hideBanner: next.hideBanner,
      });
      setPipelines(prev => prev.map(p => p.id === selectedPipelineId ? {
        ...p,
        title: next.title,
        description: next.description,
        background: next.background,
        hideBanner: next.hideBanner ?? true,
      } : p));
      toast.success('Pipeline settings updated');
    } catch (err) {
      console.error('Failed to update pipeline settings:', err);
      toast.error('Failed to update pipeline settings');
    }
  };

  // Board state for the selected pipeline
  const {
    state, loading: boardLoading, error,
    addDeal, updateDeal, moveDeal, markDealWon, markDealLost, reopenDeal, deleteDeal,
    addStage, updateStageTitle, updateStageBgColor, deleteStage, reorderStages,
  } = usePipelineState(selectedPipelineId || '');

  const filterDeal = (id: string) => {
    const deal = state.dealsById?.[id];
    if (!deal) return false;
    
    const matchesSearch = !searchQuery || 
      deal.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (deal.company?.name && deal.company.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (deal.contact?.name && deal.contact.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || deal.status === statusFilter;
    return matchesSearch && matchesStatus;
  };

  const filteredState = {
    ...state,
    stages: Object.fromEntries(
      Object.entries(state.stages || {}).map(([stageId, dealIds]) => [
        stageId,
        dealIds.filter(filterDeal)
      ])
    ),
    won: (state.won || []).filter(filterDeal),
    lost: (state.lost || []).filter(filterDeal),
  };

  const stages = state.stageOrder.map(id => state.stageMeta[id]).filter(Boolean);

  const handleMobileMenuToggle = () => {
    const crmItem = navigationItems.find(i => i.id === 'crm');
    if (crmItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmItem.subItems, crmItem.label);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="flex flex-col h-[100dvh] lg:h-[calc(100vh-3.5rem)] overflow-hidden">
      <Helmet>
        <title>Deals | AgencyPlus</title>
        <meta name="description" content="Manage your deals and track stages" />
        <link rel="canonical" href="/deals" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={() => setAddDealOpen(true)}
          searchValue=""
          onSearchChange={() => {}}
          searchPlaceholder="Search deals..."
          showSearch={false}
          showAdd
          isDarkMode={isGlobalDarkMode}
        />
      )}

      {/* Header */}
      <div className={`relative z-20 shrink-0 px-6 md:px-10 py-6 flex items-center justify-between gap-4 ${isMobile ? 'pt-20' : ''}`}>
        {/* Left: View toggle (Kanban, Calendar, Table) */}
        <div className="flex items-center gap-1 bg-muted/50 border border-border/40 rounded-lg p-1">
          <button
            onClick={() => setViewMode('kanban')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === 'kanban'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <LayoutDashboard className="h-4 w-4" />
            Kanban
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === 'calendar'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <CalendarIcon className="h-4 w-4" />
            Calendar
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === 'table'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Table2 className="h-4 w-4" />
            Table
          </button>
        </div>

        {/* Right: actions */}
        <div className={`flex items-center gap-2 shrink-0 ${isMobile ? 'hidden' : ''}`}>
          {/* Search — collapsible */}
          <div className="relative flex items-center h-10">
            <AnimatePresence initial={false}>
              {searchOpen ? (
                <motion.div
                  key="search-open"
                  className="relative flex items-center h-10"
                  initial={{ width: 40, opacity: 0.6 }}
                  animate={{ width: 256, opacity: 1 }}
                  exit={{ width: 40, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search deals..."
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border focus:ring-1 focus:ring-workspace-accent/30"
                    autoFocus
                  />
                  <button
                    onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <motion.button
                  key="search-icon"
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  title="Search deals"
                  aria-label="Search deals"
                >
                  <Search className="w-4 h-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Gear Settings: Pipeline & Stage Management Dialog */}
          <HeaderSettingsDialog
            value={pipelineConfig}
            onSave={handleUpdatePipelineConfig}
            iconOnly
            itemType="stage"
            defaultTab="columns"
            columnOrder={state.stageOrder}
            columnMeta={state.stageMeta as any}
            columnTasks={state.stages}
            onAddColumn={addStage}
            onDeleteColumn={deleteStage}
            onReorderColumns={reorderStages}
            onUpdateColumnTitle={updateStageTitle}
            onUpdateColumnBgColor={updateStageBgColor}
          />

          {/* Filter Toggle — Icon only */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Filter deals"
                title={statusFilter === 'all' ? 'Filter deals' : `Filter: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
                className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
                  statusFilter !== 'all'
                    ? 'bg-workspace-accent text-white border-transparent'
                    : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Filter className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover border-border">
              <DropdownMenuItem onClick={() => setStatusFilter('all')} className="flex items-center justify-between cursor-pointer">
                All Deals {statusFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('active')} className="flex items-center justify-between cursor-pointer">
                Active Deals {statusFilter === 'active' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('won')} className="flex items-center justify-between cursor-pointer">
                Won {statusFilter === 'won' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatusFilter('lost')} className="flex items-center justify-between cursor-pointer">
                Lost {statusFilter === 'lost' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Add Deal */}
          <button
            onClick={() => setAddDealOpen(true)}
            className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span className="font-medium">Add Deal</span>
          </button>
        </div>
      </div>



      {/* Board */}
      <div className="flex-1 overflow-hidden px-6 md:px-10 pb-8">
        {boardLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-muted-foreground animate-pulse text-sm">Loading deals...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-400 text-sm">{error}</div>
        ) : viewMode === 'kanban' ? (
          <PipelineBoard
            state={filteredState}
            onMoveDeal={moveDeal}
            onAddDeal={addDeal}
            onUpdateDeal={updateDeal}
            onMarkWon={markDealWon}
            onMarkLost={markDealLost}
            onReopenDeal={reopenDeal}
            onDeleteDeal={deleteDeal}
            onUpdateStageTitle={updateStageTitle}
            onUpdateStageBgColor={updateStageBgColor}
            onDeleteStage={deleteStage}
            onReorderStages={reorderStages}
          />
        ) : viewMode === 'calendar' ? (
          <DealCalendarView
            state={filteredState}
            onDealClick={(deal) => setSelectedDeal(deal)}
            onAddDeal={() => setAddDealOpen(true)}
          />
        ) : (
          <DealTableView
            state={filteredState}
            onDealClick={(deal) => setSelectedDeal(deal)}
          />
        )}
      </div>

      {/* Add deal dialog */}
      <NewDealDialog
        open={addDealOpen}
        onOpenChange={setAddDealOpen}
        stages={stages}
        onSubmit={async (data) => {
          await addDeal(data);
          setAddDealOpen(false);
        }}
      />

      {/* Deal detail dialog for Calendar & Table views */}
      <DealDetailDialog
        deal={selectedDeal}
        stages={stages}
        open={!!selectedDeal}
        onOpenChange={(o) => { if (!o) setSelectedDeal(null); }}
        onUpdate={async (id, updates) => {
          const { _localOnly, ...dbUpdates } = updates;
          if (!_localOnly && Object.keys(dbUpdates).length > 0) {
            await updateDeal(id, dbUpdates);
          }
          setSelectedDeal(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              ...updates,
              ...(updates.stageId !== undefined ? { stage_id: updates.stageId } : {})
            };
          });
        }}
        onMarkWon={async (id, reason) => { await markDealWon(id, reason); setSelectedDeal(null); }}
        onMarkLost={async (id, reason) => { await markDealLost(id, reason); setSelectedDeal(null); }}
        onReopen={async (id, stageId) => { await reopenDeal(id, stageId); setSelectedDeal(null); }}
        onDelete={async (id) => { await deleteDeal(id); setSelectedDeal(null); }}
      />
    </div>
  );
}
